/*
  Product cards (stage א7, notes 5, 17, 18), like Factory 54:
  - A swatch shows the card in its color: the image, the links, the quick-add
    variant, and the color the heart saves with. Everywhere a card appears.
  - In a grid, the images of the shown color sit in a row: swiped on the
    phone, and on desktop moved with two arrows that show on hover, like
    Factory 54's cards. The thin black bar under them follows the image shown
    (on desktop it shows on hover). Carousels keep one image, with the model
    image on hover.
  The colors come from the JSON at the end of each card (snippets/product-card).
*/
(() => {
  const WIDTHS = [300, 450, 600, 900];
  const srcset = (url) => WIDTHS.map((w) => `${url.replace(/width=\d+/, `width=${w}`)} ${w}w`).join(', ');

  const data = (card) => {
    if (!card.cardData) {
      try {
        card.cardData = JSON.parse(card.querySelector('[data-card-data]')?.textContent || '{}');
      } catch {
        card.cardData = {};
      }
    }
    return card.cardData;
  };

  const entryFor = (card, name) => {
    const colors = data(card).c || [];
    return colors.find((entry) => entry.n === name) || colors[0];
  };

  const setImage = (img, url) => {
    img.src = url;
    img.srcset = srcset(url);
  };

  const updateBar = (card, count, index) => {
    const bar = card.querySelector('.card__bar');
    if (!bar) return;
    bar.hidden = count < 2;
    bar.style.setProperty('--count', String(Math.max(count, 1)));
    bar.style.setProperty('--index', String(index));
  };

  const fileOf = (url) => (url || '').split('?')[0].replace(/^(https?:)?\/\/[^/]+/, '');

  /**
   * The swipe row of a grid card: one image per slide, lazy after the first.
   * The image the page already loaded stays as the first slide when it is the
   * same file, so the card never shows an empty frame while a copy loads.
   */
  const buildTrack = (card, images) => {
    const link = card.querySelector('.card__image-link');
    const first = link.querySelector('img');
    const sizes = first?.getAttribute('sizes') || '50vw';
    const slides = images.map((url, index) => {
      if (index === 0 && first && fileOf(first.getAttribute('src')) === fileOf(url)) {
        first.className = 'card__slide';
        return first;
      }
      const img = document.createElement('img');
      img.className = 'card__slide';
      img.alt = '';
      img.sizes = sizes;
      img.loading = index === 0 && first?.loading === 'eager' ? 'eager' : 'lazy';
      img.decoding = 'async';
      setImage(img, url);
      return img;
    });
    link.replaceChildren(...slides);
    link.scrollLeft = 0;
    card.classList.toggle('is-single', images.length < 2);
    updateBar(card, images.length, 0);
  };

  const arrow = (card, step) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `icon-button icon-button--solid card__nav card__nav--${step > 0 ? 'next' : 'prev'}`;
    button.tabIndex = -1;
    button.setAttribute('aria-hidden', 'true');
    button.innerHTML = '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 5 7 7-7 7"/></svg>';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const link = card.querySelector('.card__image-link');
      link.scrollBy({ left: step * link.clientWidth, behavior: window.AmorMotion?.scroll() || 'smooth' });
    });
    return button;
  };

  const makeSwipe = (card) => {
    if (card.classList.contains('is-swipe')) return;
    const entry = entryFor(card, card.dataset.cardCurrent);
    if (!entry || (entry.i || []).length < 2) return;
    card.classList.add('is-swipe');
    const link = card.querySelector('.card__image-link');
    buildTrack(card, entry.i);
    link.after(arrow(card, -1), arrow(card, 1));
    let frame = 0;
    link.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const count = link.children.length;
          const index = Math.round(link.scrollLeft / Math.max(link.clientWidth, 1));
          updateBar(card, count, Math.min(Math.max(index, 0), count - 1));
        });
      },
      { passive: true }
    );
  };

  /** Shows the card in a color. */
  const select = (card, name) => {
    const entry = entryFor(card, name);
    if (!entry) return;
    // The card keeps its color in data-card-current, never data-card-color: the click listener
    // below looks up closest('[data-card-color]'), and the card itself must not match (stage א8, note 4).
    card.dataset.cardCurrent = entry.n;
    card.querySelectorAll('[data-card-color]').forEach((button) => {
      const on = button.dataset.cardColor === entry.n;
      button.setAttribute('aria-pressed', String(on));
      button.querySelector('.swatch')?.classList.toggle('is-selected', on);
    });
    if (entry.u) card.querySelectorAll('[data-card-link]').forEach((link) => (link.href = entry.u));
    const plus = card.querySelector('[data-quick-add]');
    if (plus) {
      if (entry.v) plus.dataset.quickAddVariant = entry.v;
      if (entry.u) plus.dataset.fallbackUrl = entry.u;
    }
    const heart = card.querySelector('[data-wishlist-toggle]');
    if (heart && entry.n) {
      heart.dataset.wishlistColor = entry.n;
      heart.dataset.wishlistImage = entry.i?.[0] || '';
    }

    const images = entry.i || [];
    if (!images.length) return;
    if (card.classList.contains('is-swipe')) {
      buildTrack(card, images);
      return;
    }
    const link = card.querySelector('.card__image-link');
    const [main, second] = link.querySelectorAll('img.card__image');
    if (main) setImage(main, images[0]);
    if (second && images[1]) setImage(second, images[1]);
    else if (second) second.remove();
    else if (main && images[1]) {
      const extra = main.cloneNode();
      extra.classList.add('card__image--secondary');
      extra.loading = 'lazy';
      setImage(extra, images[1]);
      link.append(extra);
    }
    updateBar(card, images.length, 0);
  };

  const init = (card) => {
    if (card.cardReady) return;
    card.cardReady = true;
    const pressed = card.querySelector('[data-card-color][aria-pressed="true"]');
    card.dataset.cardCurrent = card.dataset.initialColor || pressed?.dataset.cardColor || data(card).c?.[0]?.n || '';
    if (card.dataset.initialColor) select(card, card.dataset.initialColor);
    if (card.closest('.product-grid')) makeSwipe(card);
  };

  const scan = (root) => {
    if (root.matches?.('[data-card]')) init(root);
    root.querySelectorAll?.('[data-card]').forEach(init);
  };

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-card-color]');
    if (!swatch) return;
    const card = swatch.closest('[data-card]');
    if (!card) return;
    event.preventDefault();
    init(card);
    select(card, swatch.dataset.cardColor);
  });

  const start = () => {
    scan(document);
    new MutationObserver((records) =>
      records.forEach((record) => record.addedNodes.forEach((node) => node.nodeType === 1 && scan(node)))
    ).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.AmorCard = { select, init };
})();
